import { TrashIcon } from '@heroicons/react/24/outline';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { upperFirst } from 'lodash';
import { stringify } from 'query-string';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';

import useDebounce from '@dashboard/hooks/useDebounce';
import useSearchInput from '@dashboard/hooks/useSearchInput';
import useUrlQuery from '@dashboard/hooks/useUrlQuery';

import { confirm } from '@dashboard/lib/confirm';
import { templateCategoriesKey, templatesKey } from '@dashboard/lib/queryKeys';

import { fetcher, fetchInfinite } from '@dashboard/services/request';

import BottomScrollListener from '@dashboard/components/BottomScrollListener';
import Button from '@dashboard/components/Button';
import { FormElement, Label, Select, TextInput } from '@dashboard/components/Form';
import Link from '@dashboard/components/Link';

import Circle from '../components/Circle';
import Table from '../components/Table';

const Payments = () => {
  const { token } = useAuth();
  const history = useHistory();

  const query = useUrlQuery();

  const { searchInput, setSearchInput, search } = useSearchInput();
  const [sortBy, setSortBy] = useState(query.sortBy || 'order');
  const [sortDirection, setSortDirection] = useState(query.sortDirection || 1);
  const queryClient = useQueryClient();
  const [category, setCategory] = useState(query.category || '');

  useEffect(() => {
    history.replace({
      search: stringify({
        sortBy,
        sortDirection,
        ...(category && { category }),
        ...(search && { search }),
      }),
    });
  }, [sortBy, sortDirection, category, search]);

  const { data: categories } = useQuery({
    queryKey: templateCategoriesKey,
    queryFn: () =>
      fetcher(
        `/templates/categories?${stringify({
          sort: JSON.stringify({ order: 1 }),
          limit: 99,
        })}`,
        token,
      ),
    enabled: !!token,
  });

  const { data, fetchNextPage, isLoading } = useInfiniteQuery({
    queryKey: ['templates', sortBy, sortDirection, category, search],
    queryFn: async ({ pageParam }) => {
      return fetchInfinite(
        `/templates?${stringify({
          ...(category && { category }),
          ...(search && { search }),
          sort: JSON.stringify({
            [sortBy]: sortDirection,
          }),
          limit: 20,
          skip: pageParam * 20,
        })}`,
        token,
      );
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) return undefined;

      return allPages.length;
    },
    refetchOnMount: true,
  });

  const updateTemplateMutation = useMutation({
    mutationFn: async ({ _id, values }) => {
      const { data } = await fetcher(`/templates/${_id}`, token, {
        method: 'PUT',
        body: JSON.stringify(values),
      });

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templatesKey });
    },
    onError: error => {
      window.alert(error.message);
    },
  });

  const deleteTemplateMutation = useMutation({
    mutationFn: async _id => {
      await fetcher(`/templates/${_id}`, token, {
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templatesKey });
    },
    onError: error => {
      window.alert(error.message);
    },
  });

  function onSort(newSortBy) {
    const oppositeSortDirection = sortDirection * -1;

    setSortDirection(sortBy === newSortBy ? oppositeSortDirection : -1);
    setSortBy(newSortBy);
  }

  return (
    <div className="pr-4">
      <div className="flex items-end justify-end gap-x-2">
        <FormElement className="!mb-0">
          <Label htmlFor="search">Search</Label>
          <div className="w-64">
            <TextInput
              border
              name="search"
              onChange={e => setSearchInput(e.target.value)}
              placeholder="Search by name, email, ID"
              value={searchInput}
            />
          </div>
        </FormElement>

        <FormElement className="!mb-0">
          <Select
            name="category"
            label="Category"
            onChange={e => setCategory(e.target.value)}
            className="w-32"
          >
            <option value="">All categories</option>
            {categories?.map(currentCategory => (
              <option
                key={currentCategory._id}
                value={currentCategory._id}
                selected={currentCategory._id === category}
              >
                {upperFirst(currentCategory.title)}
              </option>
            ))}
          </Select>
        </FormElement>

        <Button href={`/admin/templates/new`} appearance="primary">
          New template
        </Button>
      </div>
      <Table.Table>
        <Table.Head>
          <Table.Th onSort={onSort} sortKey="title" sortBy={sortBy} sortDirection={sortDirection}>
            Title
          </Table.Th>

          <Table.Th onSort={onSort} sortKey="enabled" sortBy={sortBy} sortDirection={sortDirection}>
            Enabled
          </Table.Th>

          <Table.Th
            onSort={onSort}
            sortKey="category.slug"
            sortBy={sortBy}
            sortDirection={sortDirection}
          >
            Category
          </Table.Th>

          <Table.Th
            onSort={onSort}
            sortKey="usageCount"
            sortBy={sortBy}
            sortDirection={sortDirection}
          >
            Usage count
          </Table.Th>

          <Table.Th onSort={onSort} sortKey="order" sortBy={sortBy} sortDirection={sortDirection}>
            Order
          </Table.Th>

          <Table.Th
            onSort={onSort}
            sortKey="createdAt"
            sortBy={sortBy}
            sortDirection={sortDirection}
          >
            Date created
          </Table.Th>
        </Table.Head>

        {!isLoading && (
          <BottomScrollListener onBottom={fetchNextPage} as="tbody">
            {data.pages.map(page =>
              page.data.map(template => (
                <TemplateRow
                  key={template._id}
                  template={template}
                  onChange={values => {
                    updateTemplateMutation.mutate({
                      _id: template._id,
                      values,
                    });
                  }}
                  onDelete={() => {
                    deleteTemplateMutation.mutate(template._id);
                  }}
                />
              )),
            )}
          </BottomScrollListener>
        )}
      </Table.Table>
    </div>
  );
};

function TemplateRow({ template, onChange, onDelete }) {
  const [order, setOrder] = useState(template.order);
  const debouncedOrder = useDebounce(order, 300);

  useEffect(() => {
    if (debouncedOrder === template.order) return;

    onChange({
      order: debouncedOrder,
    });
  }, [debouncedOrder]);

  return (
    <Table.Row key={template._id}>
      <Table.Td>
        <Link href={`/admin/templates/${template._id}`}>{template.title}</Link>
      </Table.Td>
      <Table.Td>
        <Circle color={template.enabled ? 'green' : 'red'} />
      </Table.Td>
      <Table.Td>{template.category?.title}</Table.Td>
      <Table.Td>{template.usageCount || 0}</Table.Td>
      <Table.Td>
        <TextInput
          type="number"
          value={order}
          onChange={e => {
            setOrder(e.target.value);
          }}
        />
      </Table.Td>
      <Table.Td>
        <div className="flex">
          <div className="flex-1">{format(new Date(template.createdAt), 'd MMM, yyyy')}</div>

          <Button
            onClick={async () => {
              const proceed = await confirm('Delete this template?');

              if (!proceed) return;

              onDelete();
            }}
            appearance="bare"
            className="ml-2"
            size="small"
          >
            <TrashIcon className="w-4 h-4" />
          </Button>
        </div>
      </Table.Td>
    </Table.Row>
  );
}

export default Payments;
